import {
  AbsoluteCenter,
  Box,
  Collapse,
  Flex,
  Heading,
  IconButton,
  Link,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { getCustomerPipelineApi } from 'api/customerCockpitProfileApi';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  formatPipelineStageHeader,
  getPipelineStageTheme,
  sortPipelineStages,
} from './pipelineHelpers';

const COLUMN_WIDTH = '280px';

function PipelineKanbanColumn({ stage }) {
  const customers = stage.customers || [];
  const theme = getPipelineStageTheme(stage.status);
  const { isOpen, onToggle } = useDisclosure({
    defaultIsOpen: customers.length > 0,
  });

  return (
    <Flex
      direction="column"
      flexShrink={0}
      w={COLUMN_WIDTH}
      minW={COLUMN_WIDTH}
      maxH="calc(100vh - 180px)"
      bg={theme.columnBg}
      borderRadius="xl"
      overflow="hidden"
    >
      <Flex
        align="center"
        justify="space-between"
        gap={2}
        px={3}
        py={3}
        bg={theme.headerBg}
        cursor="pointer"
        onClick={onToggle}
      >
        <Flex align="center" gap={2} minW={0} flex="1">
          <Box
            w="10px"
            h="10px"
            borderRadius="full"
            bg={theme.dot}
            flexShrink={0}
          />
          <Text
            fontSize="xs"
            fontWeight="700"
            letterSpacing="0.04em"
            color={theme.headerText}
            noOfLines={2}
            lineHeight="short"
          >
            {formatPipelineStageHeader(stage.status)}
          </Text>
          <Box
            px={2}
            py="2px"
            borderRadius="md"
            bg={theme.countBg}
            color={theme.countText}
            fontSize="xs"
            fontWeight="700"
            flexShrink={0}
          >
            {stage.count}
          </Box>
        </Flex>
        <IconButton
          aria-label={isOpen ? 'Collapse column' : 'Expand column'}
          icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
          size="xs"
          variant="ghost"
          color={theme.headerText}
          onClick={(event) => {
            event.stopPropagation();
            onToggle();
          }}
        />
      </Flex>

      <Collapse in={isOpen} animateOpacity style={{ overflow: 'hidden', flex: 1 }}>
        <Box
          px={2}
          py={2}
          overflowY="auto"
          maxH="calc(100vh - 240px)"
          css={{
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': {
              background: '#CBD5E1',
              borderRadius: '999px',
            },
          }}
        >
          {customers.length === 0 ? (
            <Text
              px={2}
              py={4}
              color="gray.500"
              fontSize="sm"
              fontStyle="italic"
              textAlign="center"
            >
              No customers
            </Text>
          ) : (
            customers.map((customer) => (
              <Box
                key={customer.customerId}
                mb={2}
                px={3}
                py={3}
                bg="white"
                borderRadius="lg"
                borderWidth="1px"
                borderColor="white"
                boxShadow="0 1px 2px rgba(15, 23, 42, 0.06)"
                transition="all 0.15s ease"
                _hover={{
                  borderColor: theme.dot,
                  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                  transform: 'translateY(-1px)',
                }}
              >
                <Link
                  as={RouterLink}
                  to={`/customers/${customer.customerId}/settings`}
                  color="gray.800"
                  fontSize="sm"
                  fontWeight="600"
                  lineHeight="short"
                  noOfLines={3}
                  _hover={{ color: theme.headerText, textDecoration: 'none' }}
                >
                  {customer.companyName}
                </Link>
              </Box>
            ))
          )}
        </Box>
      </Collapse>
    </Flex>
  );
}

function CustomerPipeline() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    const loadReport = async () => {
      setLoading(true);
      try {
        const data = await getCustomerPipelineApi();
        setReport(data);
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to load customer pipeline report',
          status: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [toast]);

  if (loading) {
    return (
      <AbsoluteCenter>
        <Spinner />
      </AbsoluteCenter>
    );
  }

  const stages = sortPipelineStages(report?.stages || []);

  return (
    <Box maxW="100%">
      <Heading size="lg" mb={6}>
        Customer Pipeline
      </Heading>

      <Box
        overflowX="auto"
        pb={4}
        css={{
          '&::-webkit-scrollbar': { height: '8px' },
          '&::-webkit-scrollbar-thumb': {
            background: '#CBD5E1',
            borderRadius: '999px',
          },
        }}
      >
        <Flex gap={3} align="stretch" minW="min-content" py={1}>
          {stages.map((stage) => (
            <PipelineKanbanColumn key={stage.status} stage={stage} />
          ))}
        </Flex>
      </Box>
    </Box>
  );
}

export default CustomerPipeline;
